import { observable, computed, action } from 'mobx';
import { WebGivingPage } from '../index';
import { WebGivingMerchant, PaymentMethodType, WebGivingPaymentResultStatus, WebGivingPaymentMethodModel } from '../../webgiving-generated';
import { IWebGivingPaymentState, IWebGivingRecurringPaymentInfo, GiftType } from '../../webgiving-payment-state';
import { FormField } from '../../components/form-field';
import { ValidationState, validateFields } from '../../validation/view-model-validator';
import {
	RuleType,
	createValidatorFunc,
	fieldValidationSuccess,
	FieldValidationFailure,
} from '../../validation/validation-rules';
import { WebGivingErrorType } from '../../components/error-message/error-message';
import { getSelectedFundName, getSelectedFundKey } from '../../utils/reference-field-helper';
import { dateOfGiftForDisplay } from '../../utils/onetime-gift-utils';
import { WebGivingPageViewModelBase } from '../page-view-model-base';


const blockingPaymentFailures = [
	WebGivingPaymentResultStatus.InvalidAmount,
	WebGivingPaymentResultStatus.MaximumYearExceeded,
	WebGivingPaymentResultStatus.CardFail,
	WebGivingPaymentResultStatus.PaymentMethodNotSupported,
];
const failuresResolvedByNewPaymentMethod = [
	WebGivingPaymentResultStatus.InvalidAmount,
	WebGivingPaymentResultStatus.CardFail,
	WebGivingPaymentResultStatus.PaymentMethodNotSupported,
];

export class PageConfirmGiftViewModel extends WebGivingPageViewModelBase {
	readonly type: WebGivingPage.ConfirmGift = WebGivingPage.ConfirmGift;

	amount: number;

	@observable
	validationState?: ValidationState;

	@observable
	payerElectedToCoverFees: FormField<boolean>;

	@observable
	authorizeDebitAccount: FormField<boolean>;

	@observable
	paymentMethodId: FormField<string>;

	@computed
	get paymentBlocked(): boolean {
		return this.isBlocked || (this.error && this.error.errorType === WebGivingErrorType.PaymentError ? blockingPaymentFailures.indexOf(this.error.errorReason) !== -1 : false);
	}

	@computed
	get calculatedAmountWithFees() {
		if (this.payerElectedToCoverFees.value) {
			var totalAmount = this.calculateFee(this.amount, this.merchantInfo.TransactionFeeRate);
			return Number(totalAmount.toFixed(2));
		} else {
			return this.amount;
		}
	}

	@computed
	get merchantSupportsCard(): boolean {
		return this.merchantInfo.SupportedCardBrandKeys && this.merchantInfo.SupportedCardBrandKeys.length > 0;
	}

	@observable
	merchantSupportsACH: boolean;

	recurringInfo: IWebGivingRecurringPaymentInfo | null = null;

	percentageOfPaymentAddedToCoverFees: number;

	fundName: string;

	fundKey: string;

	@computed
	get paymentMethod() {
		if (!this.paymentMethodId.value) {
			return this.paymentMethods.filter(x => x.IsPreferred)[0];
		}
		const [paymentMethod] = this.paymentMethods.filter(x => x.Id === this.paymentMethodId.value);
		return paymentMethod;
	}

	@observable
	merchantSupportsPayerCoversFees: boolean;

	@observable
	responseTimeNotice: boolean;

	oneTimeGiftDateLabel: string;

	constructor(private paymentState: IWebGivingPaymentState, public paymentMethods: WebGivingPaymentMethodModel[],
				public merchantInfo: WebGivingMerchant, public showTermsAndConditions: boolean, public hideACH = false) {
		super();
		const [paymentMethod] = this.paymentMethods.filter(x => x.Id === paymentState.paymentMethodId);
		this.paymentMethodId = FormField.Unvalidated((paymentMethod && paymentMethod.Id) || this.getPreferredPaymentMethod());
		this.payerElectedToCoverFees = FormField.Unvalidated(paymentState.payerElectedToCoverFees);
		this.authorizeDebitAccount = new FormField<boolean>(false, createValidatorFunc((value: boolean) => {
			if (this.paymentMethod.PaymentMethodType !== PaymentMethodType.ACH) {
				return fieldValidationSuccess;
			}
			if (value) {
				return fieldValidationSuccess;
			}
			return new FieldValidationFailure(RuleType.Custom, 'Please select this option to authorize this payment method.');
		}, RuleType.Custom));
		this.amount = this.paymentState.amount;

		if (paymentState.giftType === GiftType.Recurring) {
			this.recurringInfo = paymentState.recurringInfo;
		}

		this.fundName = getSelectedFundName(paymentState.referenceFieldValues, merchantInfo.CustomFields);
		this.fundKey = getSelectedFundKey(paymentState.referenceFieldValues, merchantInfo.CustomFields);
		this.merchantSupportsACH = this.merchantInfo.SupportsAch;
		this.merchantSupportsPayerCoversFees = this.merchantInfo.SupportsPayerCoversFees;
		this.payerElectedToCoverFees.value = this.merchantInfo.PayerCoversFeesDefault;
		this.oneTimeGiftDateLabel = this.generateOneTimeGiftDateLabel(paymentState);
	}

	validate(): ValidationState {
		return validateFields(this);
	}

	@action.bound
	handlePaymentMethodChange() {
		if (this.error && this.error.errorType === WebGivingErrorType.PaymentError && failuresResolvedByNewPaymentMethod.indexOf(this.error.errorReason) !== -1) {
			this.error = undefined;
		}
	}

	@action.bound
	setResponseTimeNotice(base: boolean) {
		this.responseTimeNotice = base;
	}

	private calculateFee(base: number, rate: number): number {
		const baseCents = base * 100;
		const rateThousands = rate * 1000;
		const feeThousandCents = baseCents * rateThousands;
		const totalCents = baseCents + feeThousandCents / 1000;
		return totalCents / 100;
	}

	private getPreferredPaymentMethod() {
		let [preferredPaymentMethod] = this.paymentMethods.filter(x => x.IsPreferred);
		if (!preferredPaymentMethod) {
			preferredPaymentMethod = this.paymentMethods[0];
		}

		return preferredPaymentMethod.Id;
	}

	// Generates the gift date label for displaying using our inbuilt formatter.
	// If gift type is recurring or not a one-off future gift, returns empty string.
	private generateOneTimeGiftDateLabel = (paymentState: IWebGivingPaymentState): string => {
		if (paymentState.giftType === GiftType.Recurring || !paymentState.isOneTimeFutureGift) {
			return '';
		}

		return dateOfGiftForDisplay(paymentState.giftDate);
	}
}
